import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import {
  buildDeliveryAttributePayload,
  DeliveryRunPanel,
  type DeliverySelection,
} from '~/components/common/delivery-run-panel';
import { LanguageApprovalSection } from '~/features/catalog-operations/components/translations/language-approval-section';
import { useLanguageApproval } from '~/features/catalog-operations/hooks/use-language-approval';
import { useSubmitScheduledPipelineApproval } from '~/features/content-enrichment/hooks/use-content-scoring';
import type { EnrichmentRunConfig } from '~/features/content-enrichment/types/enrichment-run-config';
import type { ScheduledPipelineApprovalPayload } from '~/features/content-enrichment/types/scheduled-enrichment.types';
import { eligibleKeysFromRunConfig } from '~/features/content-enrichment/utils/delivery-eligible-keys';

interface Props {
  open: boolean;
  /** Job uuid (route `$batchId`) — used only to invalidate the right batch-detail query. */
  jobId: string;
  /** The delivery pipeline's own workflow id (`BatchDetail.pipelineWorkflowId`) —
   *  the target for the approval endpoint. Read straight off the batch detail, so
   *  no job-status round-trip is needed. `null` on legacy runs that don't echo it,
   *  in which case the panel blocks submission with an error. */
  pipelineWorkflowId: string | null;
  /** Run configuration echoed on the batch detail — narrows the attribute picker
   *  to what this run enriched. Absent → the panel shows the full taxonomy. */
  runConfig?: EnrichmentRunConfig;
  /** Batch platform — selects the attribute source (Google or Meta catalogue). */
  platform?: 'google' | 'meta';
  /** This run's always-on attributes (Meta only), echoed on the batch header —
   *  keeps agent-produced fields in the delivery picker. */
  alwaysOnAttributes?: string[];
  onClose: () => void;
}

/**
 * Delivery panel for a batch paused at AWAITING_APPROVAL. Wraps the shared
 * `DeliveryRunPanel` and records the operator's decision against the delivery
 * pipeline-approval endpoint (`POST enrichments/pipeline/{pipelineWorkflowId}/approval`),
 * attaching the chosen attribute slice. `pipelineWorkflowId` comes directly off the
 * batch detail — the same endpoint and payload the Meta enrichment flow already
 * uses — so both the Google and Meta batch flows share one approval path.
 */
export function DeliveryApprovalPanel({
  open,
  jobId,
  pipelineWorkflowId,
  runConfig,
  platform,
  alwaysOnAttributes,
  onClose,
}: Props) {
  const queryClient = useQueryClient();
  const { mutate: submitApproval, isPending: isSubmitting } = useSubmitScheduledPipelineApproval();
  // Language gate: which translated locales ship with this delivery (Google only).
  const languages = useLanguageApproval(pipelineWorkflowId, open && platform !== 'meta');

  // Which attributes this run enriched, for the picker. Absent → full taxonomy.
  const eligibleKeys = eligibleKeysFromRunConfig(runConfig);

  // The attribute slice is only meaningful for a delivery — a rejection carries
  // just the decision + notes.
  function attributesFor(selection: DeliverySelection) {
    if (selection.decision !== 'APPROVED' || selection.attributeKeys.length === 0) return {};
    return buildDeliveryAttributePayload(selection);
  }

  function handleSubmit(selection: DeliverySelection) {
    if (!pipelineWorkflowId) {
      toast.error('Unable to resolve the delivery pipeline for this batch. Please reload.');
      return;
    }
    const payload: ScheduledPipelineApprovalPayload = {
      decision: selection.decision,
      ...(selection.notes ? { notes: selection.notes } : {}),
      ...attributesFor(selection),
      ...(selection.decision === 'APPROVED' ? languages.payload : {}),
    };
    // The hook only toasts on error; success feedback + the batch-detail refetch
    // (so the pipeline stages reflect the decision) are handled here.
    submitApproval(
      { workflowId: pipelineWorkflowId, payload },
      {
        onSuccess: () => {
          toast.success(
            selection.decision === 'APPROVED' ? 'Delivery approved.' : 'Delivery rejected.',
          );
          void queryClient
            .invalidateQueries({ queryKey: queryKeys.catalogOperations.batchDetail(jobId) })
            .then(onClose);
        },
      },
    );
  }

  return (
    <DeliveryRunPanel
      testId="delivery-run-panel"
      open={open}
      {...(platform ? { platform } : {})}
      {...(eligibleKeys ? { eligibleKeys } : {})}
      {...(alwaysOnAttributes ? { alwaysOnKeys: alwaysOnAttributes } : {})}
      isSubmitting={isSubmitting}
      extraSection={
        <LanguageApprovalSection
          options={languages.options}
          selected={languages.selected}
          onToggle={languages.toggle}
          disabled={isSubmitting}
          connectionId={languages.connectionId}
        />
      }
      onSubmit={handleSubmit}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    />
  );
}
